import type { FC, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGameConfig } from '../../../config/GameConfigProvider';
import { DeviceInfo } from '../../../deviceInfo/deviceInfo';
import { useResizeEffect } from '../../../hooks/useResizeEffect';
import { useVisibilityChange } from '../../../hooks/useVisibilityChange';
import { useCommunicationDelay } from '../../../store/apiHooks/useCommunicationDelay';
import { useCoreDispatch } from '../../../store/CoreStore';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { useGameStateSlice } from '../../../store/slices/game/selectors';
import { gameStateActions } from '../../../store/slices/game/slice';

import { ITrafficLightStatus, TrafficLight } from './TrafficLight';
import { baseTrafficLightConfig } from './types';

const restartProgressbarAnimation = (element: RefObject<HTMLDivElement>): number => {
  if (!element.current) return 0;
  element.current.style.animationName = 'none';
  const clientHeight = element.current.clientHeight;
  element.current.style.removeProperty('animation-name');
  return clientHeight;
};

const resetProgressbarAnimation = (element: RefObject<HTMLDivElement>): number => {
  if (!element.current) return 0;
  element.current.style.animationPlayState = 'paused';
  const clientHeight = element.current.clientHeight;
  element.current.style.animationPlayState = 'running';
  return clientHeight;
};

interface TrafficLightProps {
  line?: boolean;
}

// TODO: Traffic light is used for back to game component, after all games will go to newMobile layout would be nice to make separate component for that with less logic
export const TrafficLightConnected: FC<TrafficLightProps> = ({ line }) => {
  const dispatch = useCoreDispatch();
  const gameCommunicationDelay = useCommunicationDelay();
  const visible = useVisibilityChange();

  const timer = useGameStateSlice.getTimer(); // in seconds
  const timerStartTime = useGameStateSlice.getTimerStartTime(); // 1701970321681 | Date.now()
  const timings = useConfigSlice.getTimings();

  const isIosChrome = useMemo(() => DeviceInfo.getCustomFlags()?.IS_IOS_CHROME, []);

  const [animationDuration, setAnimationDuration] = useState<number>(0); // in ms
  const [progressOffset, setProgressOffset] = useState<number>(0);

  const progressbarRef = useRef<HTMLDivElement>(null);
  useResizeEffect(
    useCallback(() => {
      resetProgressbarAnimation(progressbarRef);
    }, [])
  );

  const onScheduledStatusChange = useCallback(
    (nextStatus: string) => {
      dispatch(gameStateActions.setCurrentGameStatus(nextStatus));
    },
    [dispatch]
  );

  const startTimer = useCallback(
    (timer: number, timerStartTime: number, gameCommunicationDelay: number): void => {
      if (timings) {
        const elapsedMilliSeconds = Date.now() - timerStartTime;
        const timerInMilliseconds = timer * 1_000; //9 -> 9000
        const calculatedDuration = timerInMilliseconds - elapsedMilliSeconds;
        const progressOffset = 1 - calculatedDuration / (timings.bettingWindow - gameCommunicationDelay);
        setAnimationDuration(calculatedDuration);
        setProgressOffset(Math.min(1, Math.max(0, progressOffset)));

        if (visible) {
          restartProgressbarAnimation(progressbarRef);
        }
      }
    },
    [timings, visible]
  );

  const config = useGameConfig().config?.trafficLightConfig ?? baseTrafficLightConfig;
  const statusName = useGameStateSlice.getCurrentStatus().toUpperCase();
  const status = config[statusName];
  const statusWithTiming = useMemo((): ITrafficLightStatus => {
    // For case when no TrafficLightConfig passed.
    // TODO: fix with creating trafficLight for back to game
    if (!status)
      return {
        calculatedScheduledStatusChange: {
          timeout: 0,
          nextStatus: '',
        },
        label: '',
        rendersTimer: false,
      };
    if (!status.scheduledStatusChange || !timings) {
      return status;
    } else if (typeof status.scheduledStatusChange.timeout === 'number') {
      return {
        ...status,
        calculatedScheduledStatusChange: {
          timeout: status.scheduledStatusChange.timeout,
          nextStatus: status.scheduledStatusChange.nextStatus,
        },
      };
    } else if (typeof status.scheduledStatusChange.timeout === 'function') {
      return {
        ...status,
        calculatedScheduledStatusChange: {
          ...status.scheduledStatusChange,
          timeout: status.scheduledStatusChange.timeout(timings),
        },
      };
    } else {
      return {
        ...status,
        calculatedScheduledStatusChange: {
          ...status.scheduledStatusChange,
          timeout: timings[status.scheduledStatusChange.timeout],
        },
      };
    }
  }, [status, timings]);

  useEffect(() => {
    const handleTimerStart = (): void => {
      startTimer(timer, timerStartTime, gameCommunicationDelay);
    };
    window.addEventListener('resize', handleTimerStart, {
      passive: true,
    });
    window.addEventListener('orientationchange', handleTimerStart, {
      passive: true,
    });
    handleTimerStart();
    return (): void => {
      window.removeEventListener('resize', handleTimerStart);
      window.removeEventListener('orientationchange', handleTimerStart);
    };
  }, [startTimer, timer, timerStartTime]);

  return (
    <TrafficLight
      animationDuration={animationDuration}
      fallbackAnimation={isIosChrome}
      line={line}
      onScheduledStatusChange={onScheduledStatusChange}
      progressOffset={progressOffset}
      ref={progressbarRef}
      resetAnimationDurationCallback={() => {
        setAnimationDuration(0);
        setProgressOffset(1);
      }}
      status={statusWithTiming}
      timerStartTime={timerStartTime}
    />
  );
};
